/**
 * PersonaContext — TTS centralisé (voix de synthèse FR).
 *
 * Phase 1 C (plan §4.5) : TTS navigateur FIABILISÉ.
 *   - détection ÉLARGIE d'une voix française réelle (fr-FR / fr_FR / espeak / …) ;
 *   - chargement des voix garanti avant le 1er `speak` (Chrome les charge en
 *     async ; certains moteurs n'émettent jamais `voiceschanged`) ;
 *   - JAMAIS muet silencieux : si aucune voix fr → on parle quand même avec la
 *     voix par défaut + diagnostic `ttsStatus = 'unavailable'` (badge UI).
 *
 * Hors scope (Phase 3) : TTS serveur Piper.
 */

import {
  createContext,
  useContext,
  useRef,
  useEffect,
  useCallback,
  useState,
  type ReactNode,
} from 'react';

// DRY mirror — contrat : README de Horaliia/ai-engine (APP-H/plugins/ai-engine/README.md,
// WAKE_WORD_VARIANTS). Anti-désync : incident V1 (variantes 4/5 vs 5/5) tracé.
export const WAKE_WORDS = ['saisi', 'saisis'];

const VOICE_PITCH = 1.10;
const VOICE_RATE  = 0.95;
/** Débit estimé du TTS (caractères/seconde) pour le timer de secours `onEnd` (E1). */
const TTS_CHARS_PER_SEC = 13;
/** Noms de voix fr préférés (ordre de priorité), comparés en minuscules. */
const PREFERRED_VOICE_HINTS = ['google français', 'français', 'french', 'espeak'];

/** `pending` = voix pas encore sondées ; `ready` = voix fr trouvée ; `unavailable` = aucune. */
export type TtsStatus = 'pending' | 'ready' | 'unavailable';

/** Sélectionne la meilleure voix française disponible, ou null. */
function pickFrVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  if (voices.length === 0) return null;
  for (const hint of PREFERRED_VOICE_HINTS) {
    const byName = voices.find((v) => v.name.toLowerCase().includes(hint));
    if (byName) return byName;
  }
  // Fallback : n'importe quelle voix dont la langue commence par "fr" (fr-FR, fr_FR…).
  return voices.find((v) => v.lang.toLowerCase().startsWith('fr')) ?? null;
}

interface PersonaContextValue {
  /** Énonce `text`. `onEnd` (E1 auto-écoute) est appelé UNE fois la parole finie
   *  (via `onend` natif OU timer de secours), même si le TTS est indisponible —
   *  sans quoi le dialogue se bloquerait faute de signal de fin. */
  speak: (text: string, onEnd?: () => void) => void;
  /** Interrompt immédiatement le TTS en cours SANS déclencher l'`onEnd` de
   *  l'énoncé coupé (V1 — barge-in / la voix du soignant est maître). */
  stopSpeaking: () => void;
  /** Diagnostic TTS pour l'UI (badge « voix indisponible »). */
  ttsStatus: TtsStatus;
  /** Vrai entre le début de `speak()` et la fin de parole (orbe « patiente », E2). */
  isSpeaking: boolean;
}

const PersonaContext = createContext<PersonaContextValue | null>(null);

export function PersonaProvider({ children }: { children: ReactNode }) {
  const frVoiceRef = useRef<SpeechSynthesisVoice | null>(null);
  const [ttsStatus, setTtsStatus] = useState<TtsStatus>('pending');
  // E1 auto-écoute : signal de parole en cours + fiabilisation de la fin de TTS.
  const [isSpeaking, setIsSpeaking] = useState(false);
  /** Utterance courante : on détache ses handlers avant `cancel()` (sinon `cancel`
   *  déclenche son `onend` → faux signal de fin sur l'énoncé précédent). */
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  /** Timer de secours `onEnd` (Chrome n'émet pas toujours `onend`). */
  const speakTimerRef = useRef<number | null>(null);

  // Sonde les voix ; renvoie true dès qu'au moins une voix est disponible.
  const probeVoices = useCallback((): boolean => {
    if (!window.speechSynthesis) {
      setTtsStatus('unavailable');
      return false;
    }
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return false; // pas encore chargées
    frVoiceRef.current = pickFrVoice(voices);
    setTtsStatus(frVoiceRef.current ? 'ready' : 'unavailable');
    return true;
  }, []);

  // Préchargement des voix (Chrome async). Filet de sécurité : si `voiceschanged`
  // ne se déclenche jamais mais que des voix apparaissent tard, on re-sonde.
  useEffect(() => {
    if (!window.speechSynthesis) {
      setTtsStatus('unavailable');
      return;
    }
    probeVoices();
    window.speechSynthesis.addEventListener('voiceschanged', probeVoices);
    const t = window.setTimeout(() => {
      // Après ~1.2s : si toujours rien, marque indisponible (jamais muet silencieux).
      if (!probeVoices()) setTtsStatus('unavailable');
    }, 1200);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', probeVoices);
      window.clearTimeout(t);
    };
  }, [probeVoices]);

  /** Coupe toute parole en cours : timer de secours + handlers de l'utterance
   *  (neutralisés AVANT `cancel`, sinon `cancel()` déclenche un faux `onend`) + cancel.
   *  Ne touche PAS `isSpeaking` (les appelants décident). */
  const cancelCurrent = useCallback(() => {
    if (speakTimerRef.current !== null) {
      window.clearTimeout(speakTimerRef.current);
      speakTimerRef.current = null;
    }
    if (utteranceRef.current) {
      utteranceRef.current.onend = null;
      utteranceRef.current.onerror = null;
      utteranceRef.current = null;
    }
    window.speechSynthesis?.cancel();
  }, []);

  /** V1 — interrompt le TTS immédiatement sans relancer l'`onEnd` de l'énoncé
   *  coupé (la voix du soignant est maître). */
  const stopSpeaking = useCallback(() => {
    cancelCurrent();
    setIsSpeaking(false);
  }, [cancelCurrent]);

  const speak = useCallback((text: string, onEnd?: () => void) => {
    // Clôture toute parole précédente (timer + handlers + cancel) — une seule à la fois.
    cancelCurrent();
    if (!window.speechSynthesis) {
      // TTS absent (badge « voix indisponible ») : ne JAMAIS bloquer l'auto-écoute.
      setIsSpeaking(false);
      onEnd?.();
      return;
    }
    // Garantit une dernière tentative de sonde si on parle avant le chargement.
    if (frVoiceRef.current === null) probeVoices();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang   = 'fr-FR';
    utterance.rate   = VOICE_RATE;
    utterance.pitch  = VOICE_PITCH;
    utterance.volume = 0.8;
    // Voix fr si trouvée ; sinon voix par défaut (on parle quand même).
    if (frVoiceRef.current) utterance.voice = frVoiceRef.current;

    // Fin de parole déclenchée UNE seule fois (`onend` natif OU timer de secours).
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (speakTimerRef.current !== null) {
        window.clearTimeout(speakTimerRef.current);
        speakTimerRef.current = null;
      }
      if (utteranceRef.current === utterance) utteranceRef.current = null;
      setIsSpeaking(false);
      onEnd?.();
    };
    utterance.onend = finish;
    utterance.onerror = finish;

    utteranceRef.current = utterance;
    setIsSpeaking(true);
    // Secours : Chrome n'émet pas toujours `onend` → durée estimée + 1 s (spec §2).
    const estMs = (text.length / TTS_CHARS_PER_SEC) * 1000 + 1000;
    speakTimerRef.current = window.setTimeout(finish, estMs);

    window.speechSynthesis.speak(utterance);
  }, [probeVoices, cancelCurrent]);

  return (
    <PersonaContext.Provider value={{ speak, stopSpeaking, ttsStatus, isSpeaking }}>
      {children}
    </PersonaContext.Provider>
  );
}

export function usePersona() {
  const ctx = useContext(PersonaContext);
  if (!ctx) throw new Error('usePersona doit être utilisé dans <PersonaProvider>');
  return ctx;
}
